import React, { useState, useEffect } from "react";
import {
  Flex,
  FormControl,
  Text,
  useToast,
  Switch,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import {
  white,
  newBorder,
  textPrimary,
  textSecondary,
  backgroundLight,
} from "../../../../../utils/colors";
import { useTranslation } from "react-i18next";
import FormControlCountry from "../../../../../components/input/FormControlCountry";
import FormControlDefault from "../../../../../components/input/FormControlDefault";
import useThemeColors from "../../../../../hooks/useThemeColors";
import { useSelector } from "react-redux";

const BillingInfoNew = ({
  tenant,
  address,
  setAddress,
  companyName,
  setCompanyName,
  companyCode,
  setCompanyCode,
  isIndividual,
  setIsIndividual,
  fullName,
  setFullName,
  vatCode,
  setVatCode,
}) => {
  const { t } = useTranslation();

  const toast = useToast();

  const { textPrimaryMode } = useThemeColors();

  // STATE

  // REDUX
  const dispatch = useDispatch();

  // HANDLERS

  // VALIDATION

  // USE EFFECT
  useEffect(() => {
    if (tenant) {
      if (tenant.isIndividual) {
        setIsIndividual(true);
      } else {
        setIsIndividual(false);
      }
      setFullName(tenant.fullName);
      setCompanyName(tenant.companyName);
      setCompanyCode(tenant.companyCode);
      setAddress({
        street: tenant.address?.street ? tenant.address.street : "",
        city: tenant.address?.city ? tenant.address.city : "",
        zip: tenant.address?.zip ? tenant.address.zip : "",
        country: tenant.operatingCountry ? tenant.operatingCountry : "",
      });
      setVatCode(tenant.vatCode ? tenant.vatCode : "");
    }
  }, [tenant]);

  // LOGS
  // console.log("tenant:", tenant);

  return (
    <>
      <Flex
        w="100%"
        h="100%"
        direction="column"
        overflowY="auto"
        p="0rem 0rem 0rem"
      >
        {/* BILLING DETAILS START */}
        <Flex direction="column" w="100%">
          <Flex align="center" justify="space-between" w="100%">
            <Text
              fontSize="0.875rem"
              fontWeight="600"
              color={textPrimaryMode}
              m="0 0 0.5rem"
            >
              {t("module.billingDetails")}
            </Text>
            <Flex align="center" justify="flex-end">
              <Text
                fontSize="xs"
                color={textSecondary}
                fontWeight="500"
                mr="0.5rem"
              >
                {t("tenant.isIndividual")}
              </Text>
              <Switch
                onChange={() => setIsIndividual(!isIndividual)}
                isChecked={isIndividual}
                colorScheme="primary"
                size="sm"
              />
            </Flex>
          </Flex>

          <Flex
            direction="column"
            w="100%"
            bg={backgroundLight}
            borderRadius="1rem"
            p="1.5rem 1.5rem 0.5rem"
          >
            {isIndividual ? (
              <FormControlDefault
                size="lg"
                labelFontSize="0.8rem"
                type="text"
                isRequired={true}
                label={t("form.fullName")}
                value={fullName}
                placeholder={t("placeholder.fullName")}
                onChange={(e) => setFullName(e.target.value)}
                w={["100%", "100%", "100%"]}
                errorMessage={t("formError.fullNameRequired")}
              />
            ) : (
              <FormControlDefault
                size="lg"
                labelFontSize="0.8rem"
                type="text"
                isRequired={true}
                label={t("form.companyName")}
                value={companyName}
                placeholder={t("placeholder.companyName")}
                onChange={(e) => setCompanyName(e.target.value)}
                w={["100%", "100%", "100%"]}
                errorMessage={t("formError.companyNameRequired")}
              />
            )}

            <FormControlDefault
              isLabelEnabled
              type="email"
              size="lg"
              labelFontSize="0.8rem"
              isRequired
              label={t("module.registrationEmail")}
              value={tenant.registrationEmail ? tenant.registrationEmail : ""}
              placeholder={t("placeholder.representativeEmail")}
              isDisabled
              w={["100%", "100%", "100%"]}
              errorMessage={t("formError.representativeEmailRequired")}
            />

            {!isIndividual && (
              <>
                <FormControlDefault
                  size="lg"
                  labelFontSize="0.8rem"
                  type="text"
                  isRequired={true}
                  label={t("form.companyCode")}
                  value={companyCode}
                  placeholder={t("placeholder.companyCode")}
                  onChange={(e) => setCompanyCode(e.target.value)}
                  w={["100%", "100%", "100%"]}
                  errorMessage={t("formError.companyCodeRequired")}
                />

                <FormControl
                  mb={4}
                  isRequired={false}
                  w="50%"
                  position="relative"
                  role="group"
                >
                  <FormLabel
                    fontSize="0.8rem"
                    position="relative"
                    color={textPrimary}
                  >
                    {t("form.vatCode")}
                  </FormLabel>
                  <Input
                    type="text"
                    bg={white}
                    value={vatCode}
                    placeholder={t("placeholder.vatCode")}
                    onChange={(e) => setVatCode(e.target.value)}
                    size="lg"
                    fontSize="0.938rem"
                    fontWeight="500"
                    color={textPrimary}
                    border={`1px solid ${newBorder}`}
                    borderRadius="0.75rem"
                    boxShadow="none !important"
                  />
                </FormControl>
              </>
            )}

            <>
              <Flex direction="column" align="center" justify="center" p="0rem">
                <Flex
                  gap={["0rem", "2rem", "2rem"]}
                  w="100%"
                  direction={["column", "row", "row"]}
                >
                  <FormControlDefault
                    size="lg"
                    labelFontSize="0.8rem"
                    type="text"
                    isRequired={true}
                    label={t("form.street")}
                    value={address.street}
                    placeholder={t("placeholder.street")}
                    onChange={(e) =>
                      setAddress({ ...address, street: e.target.value })
                    }
                    w={["100%", "50%", "50%"]}
                  />

                  <FormControlDefault
                    size="lg"
                    labelFontSize="0.8rem"
                    type="text"
                    isRequired={true}
                    label={t("form.zip")}
                    value={address.zip}
                    placeholder={t("placeholder.zip")}
                    onChange={(e) =>
                      setAddress({ ...address, zip: e.target.value })
                    }
                    w={["100%", "50%", "50%"]}
                    mb={0}
                  />
                </Flex>

                <Flex
                  gap={["0rem", "2rem", "2rem"]}
                  w="100%"
                  direction={["column", "row", "row"]}
                >
                  <FormControlDefault
                    size="lg"
                    labelFontSize="0.8rem"
                    type="text"
                    isRequired={true}
                    label={t("form.city")}
                    value={address.city}
                    placeholder={t("placeholder.city")}
                    onChange={(e) =>
                      setAddress({ ...address, city: e.target.value })
                    }
                    w={["100%", "50%", "50%"]}
                    errorMessage={t("formError.cityRequired")}
                  />

                  <FormControlCountry
                    size="lg"
                    labelFontSize="0.8rem"
                    label={t("form.country")}
                    value={address.country}
                    isRequired={true}
                    onChange={(selectedOption) => {
                      setAddress(
                        Object.assign({}, address, {
                          country: selectedOption.value,
                        })
                      );
                    }}
                    w={["100%", "50%", "50%"]}
                    errorMessage={t("formError.countryRequired")}
                  />
                </Flex>
              </Flex>
            </>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default BillingInfoNew;
